// Multi-organisation membership (2026-10-09; backend memberships.py,
// routers/memberships.py).
//
// - Organisation switcher: a user with more than one organisation (home plus
//   live memberships) gets an "Organisation" dropdown at the top of the
//   sidebar listing ONLY their organisations. Picking one stores it on the
//   SERVER session (POST /api/auth/organisations/switch) and reloads, so
//   every cached payload is fetched again as that organisation, with the
//   membership's role, teams and read-only (the workspace list follows).
// - Admin > Users & teams:
//     * each user row gets an "Organisations" button (platform admins): the
//       user's memberships elsewhere - add (organisation, role, teams,
//       read-only), edit, revoke;
//     * "People from other organisations": memberships OF the admin's active
//       organisation - add by exact username, edit, revoke (org admins and
//       platform admins).
import { getJson, patchJson, postJson } from "./api";
import { el } from "./legend";
import { getState, roleAtLeast } from "./state";
import type { Team } from "./state";
import { showToast } from "./toast";
import { teamPicker } from "./workspaces";

export interface OrgEntry {
  id: number;
  name: string;
  slug: string | null;
  role: string;
  teams: Team[];
  read_only: boolean;
  home: boolean;
  active: boolean;
  membership_id: number | null;
}

export interface ActiveMembership {
  membership_id: number;
  client_id: number;
  client_name: string;
  role: string;
  teams: Team[];
  read_only: boolean;
}

export interface Membership {
  id: number;
  user_id: number;
  username: string;
  display_name: string;
  client_id: number;
  client_name: string;
  role: string;
  teams: Team[];
  read_only: boolean;
  granted_at: string;
  granted_by: string | null;
  updated_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  note: string | null;
  live: boolean;
}

interface MembershipOptions {
  clients: { id: number; name: string; slug: string }[];
  roles: string[];
  can_pick_client: boolean;
  default_client_id: number;
}

type MembershipList = MembershipOptions & { memberships: Membership[] };

interface GrantBody {
  client_id?: number;
  user_id?: number;
  username?: string;
  role: string;
  teams: Team[];
  read_only: boolean;
  note?: string;
}

const ADMIN = "/api/admin/memberships";

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// ---------------------------------------------------------------------------
// Switcher
// ---------------------------------------------------------------------------

function userOrgs(): OrgEntry[] {
  const u = getState().currentUser as (ReturnType<typeof getState>["currentUser"] & { organisations?: OrgEntry[] }) | null;
  return u?.organisations ?? [];
}

function activeMembership(): ActiveMembership | null {
  const u = getState().currentUser as { active_membership?: ActiveMembership | null } | null;
  return u?.active_membership ?? null;
}

async function switchOrg(clientId: number | null): Promise<void> {
  try {
    await postJson("/api/auth/organisations/switch", { client_id: clientId });
    navigator.serviceWorker?.controller?.postMessage("clear-api-cache");
    window.location.reload();
  } catch (e) {
    showToast(`Couldn't switch organisation: ${errText(e)}`);
  }
}

/** The member's organisation dropdown (+ a slim "Working in <org>" banner
 * while switched). No-op for platform admins (they have "View as") and for
 * users with only a home organisation. Never throws. */
export function initMembershipSwitcher(): void {
  const user = getState().currentUser;
  if (!user || roleAtLeast(user, "platform_admin")) return;
  const orgs = userOrgs();
  const active = activeMembership();
  if (active) renderMemberBanner(active, orgs.find((o) => o.home)?.name ?? "your organisation");
  if (orgs.length < 2) return;
  const sidebar = document.getElementById("sidebar");
  if (!sidebar || document.getElementById("org-switcher")) return;
  const box = el("div", { id: "org-switcher", className: "workspace-switcher org-switcher" });
  const label = el("label", { htmlFor: "org-select" }, "Organisation");
  const select = el("select", { id: "org-select" });
  select.title = "Switch the organisation you are working in. Your role and teams there come from your access to it.";
  for (const o of orgs) {
    const opt = el("option", { value: String(o.id) });
    opt.textContent = `${o.name}${o.home ? " (home)" : ""} · ${o.role}${o.read_only ? ", read-only" : ""}`;
    opt.selected = o.active;
    select.append(opt);
  }
  select.addEventListener("change", () => {
    const pick = orgs.find((o) => String(o.id) === select.value);
    select.disabled = true;
    void switchOrg(pick && !pick.home ? pick.id : null);
  });
  box.append(label, select);
  const ws = document.getElementById("workspace-switcher");
  if (ws) ws.before(box);
  else sidebar.prepend(box);
}

function renderMemberBanner(active: ActiveMembership, homeName: string): void {
  const header = document.getElementById("header");
  if (!header || document.getElementById("acting-banner")) return;
  const bar = el("div", { id: "acting-banner", className: "acting-banner member" });
  bar.setAttribute("role", "status");
  const text = el("span", { className: "acting-banner-text" });
  const strong = el("strong", {}, active.client_name);
  text.append("Working in ", strong, ` as ${active.role}${active.read_only ? " (read-only)" : ""}`);
  const back = el("button", { type: "button", className: "acting-banner-return" }, `Return to ${homeName}`);
  back.addEventListener("click", () => {
    back.disabled = true;
    void switchOrg(null);
  });
  bar.append(text, back);
  header.classList.add("acting");
  header.prepend(bar);
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

function roleSelect(roles: string[], value: string): HTMLSelectElement {
  const s = el("select");
  for (const r of roles) {
    const o = el("option", { value: r }, r);
    o.selected = r === value;
    s.append(o);
  }
  return s;
}

function membershipRow(m: Membership, roles: string[], showOrg: boolean, onChange: () => void): HTMLTableRowElement {
  const tr = el("tr");
  if (showOrg) tr.append(el("td", {}, m.client_name));
  else tr.append(el("td", {}, `${m.username} (${m.display_name})`));
  if (!m.live) {
    tr.append(
      el("td", { className: "caption" }, m.role),
      el("td", { className: "caption" }, m.teams.join(", ") || "-"),
      el("td", { className: "caption" }, m.read_only ? "yes" : "no"),
      el("td", { className: "caption" }, `Revoked ${new Date(m.revoked_at ?? "").toLocaleDateString()} by ${m.revoked_by ?? "?"}`),
    );
    return tr;
  }
  const save = (body: Record<string, unknown>) =>
    patchJson(`${ADMIN}/${m.id}`, body)
      .then(onChange)
      .catch((e) => {
        showToast(`Couldn't update: ${errText(e)}`);
        onChange();
      });
  const rs = roleSelect(roles, m.role);
  rs.addEventListener("change", () => {
    rs.disabled = true;
    void save({ role: rs.value });
  });
  const tp = teamPicker(m.teams, (teams) => void save({ teams }));
  const ro = el("input", { type: "checkbox", checked: m.read_only });
  ro.addEventListener("change", () => {
    ro.disabled = true;
    void save({ read_only: ro.checked });
  });
  const revoke = el("button", { type: "button" }, "Revoke");
  revoke.addEventListener("click", () => {
    if (!window.confirm(`Revoke ${m.username}'s access to ${m.client_name}? Their sessions there go back home at once.`)) return;
    revoke.disabled = true;
    postJson(`${ADMIN}/${m.id}/revoke`, {})
      .then(onChange)
      .catch((e) => {
        showToast(`Couldn't revoke: ${errText(e)}`);
        revoke.disabled = false;
      });
  });
  const tdRole = el("td");
  tdRole.append(rs);
  const tdTeams = el("td");
  tdTeams.append(tp.el);
  const tdRo = el("td");
  tdRo.append(ro);
  const tdAct = el("td", { className: "caption" });
  tdAct.append(`Granted ${new Date(m.granted_at).toLocaleDateString()} by ${m.granted_by ?? "?"} `, revoke);
  tr.append(tdRole, tdTeams, tdRo, tdAct);
  return tr;
}

function membershipTable(rows: Membership[], roles: string[], showOrg: boolean, onChange: () => void): HTMLElement {
  const table = el("table", { className: "detail-table" });
  const head = el("tr");
  head.append(
    el("th", {}, showOrg ? "Organisation" : "User"),
    el("th", {}, "Role"),
    el("th", {}, "Teams"),
    el("th", {}, "Read-only"),
    el("th", {}, ""),
  );
  table.append(head);
  for (const m of rows) table.append(membershipRow(m, roles, showOrg, onChange));
  const wrap = el("div", { className: "admin-table-scroll" });
  wrap.append(table);
  return wrap;
}

function grantForm(opts: MembershipOptions, fixed: { userId?: number }, onDone: () => void, exclude: number[] = []): HTMLElement {
  const form = el("form");
  form.style.cssText = "display:flex;flex-wrap:wrap;gap:0.4rem;align-items:center;margin-top:0.4rem";
  let usernameInput: HTMLInputElement | null = null;
  if (fixed.userId === undefined) {
    usernameInput = el("input", { type: "text", placeholder: "Exact username", required: true });
    form.append(usernameInput);
  }
  let orgSelect: HTMLSelectElement | null = null;
  if (opts.can_pick_client) {
    orgSelect = el("select");
    for (const c of opts.clients) {
      if (exclude.includes(c.id)) continue;
      const o = el("option", { value: String(c.id) }, c.name);
      o.selected = fixed.userId === undefined && c.id === opts.default_client_id;
      orgSelect.append(o);
    }
    form.append(orgSelect);
  }
  const rs = roleSelect(opts.roles, "view");
  const tp = teamPicker([]);
  const roLabel = el("label", {});
  const ro = el("input", { type: "checkbox", checked: true });
  roLabel.append(ro, " read-only");
  const btn = el("button", { type: "submit" }, "Grant access");
  const status = el("span", { className: "caption" });
  form.append(rs, tp.el, roLabel, btn, status);
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    btn.disabled = true;
    status.textContent = "";
    const body: GrantBody = { role: rs.value, teams: tp.value(), read_only: ro.checked };
    if (fixed.userId !== undefined) body.user_id = fixed.userId;
    if (usernameInput) body.username = usernameInput.value.trim();
    if (orgSelect) body.client_id = Number(orgSelect.value);
    postJson(ADMIN, body)
      .then(onDone)
      .catch((e) => {
        status.textContent = errText(e);
        btn.disabled = false;
      });
  });
  return form;
}

/** Per-user "Organisations" toggle for an Admin > Users row (platform admins
 * only: an organisation admin can't grant their own users access elsewhere).
 * Returns the button cell content and the (initially hidden) detail row. */
export function userOrganisationsControls(userId: number, username: string, homeClientId: number | null, colSpan: number):
  { button: HTMLElement; detailRow: HTMLTableRowElement } | null {
  const me = getState().currentUser;
  if (!me || !roleAtLeast(me, "platform_admin")) return null;
  const detailRow = el("tr");
  detailRow.hidden = true;
  const cell = el("td");
  cell.colSpan = colSpan;
  detailRow.append(cell);
  const button = el("button", { type: "button", title: "Access to other organisations" }, "Organisations");
  const load = () => {
    cell.innerHTML = "";
    cell.append(el("p", { className: "caption" }, "Loading…"));
    getJson<MembershipList>(`${ADMIN}/user/${userId}`)
      .then((doc) => {
        cell.innerHTML = "";
        cell.append(el("h4", {}, `${username}: other organisations`));
        const live = doc.memberships.filter((m) => m.live);
        const past = doc.memberships.filter((m) => !m.live);
        if (doc.memberships.length) cell.append(membershipTable([...live, ...past], doc.roles, true, load));
        else cell.append(el("p", { className: "caption" }, "Home organisation only."));
        const exclude = [...live.map((m) => m.client_id), ...(homeClientId != null ? [homeClientId] : [])];
        cell.append(grantForm(doc, { userId }, load, exclude));
      })
      .catch((e) => {
        cell.innerHTML = "";
        cell.append(el("p", { className: "caption" }, `Couldn't load: ${errText(e)}`));
      });
  };
  button.addEventListener("click", () => {
    detailRow.hidden = !detailRow.hidden;
    if (!detailRow.hidden) load();
  });
  return { button, detailRow };
}

/** "People from other organisations": memberships OF the admin's active
 * organisation (org admins and platform admins). */
export function renderExternalMembersSection(root: HTMLElement): void {
  const me = getState().currentUser;
  if (!me || !roleAtLeast(me, "admin")) return;
  const section = el("div");
  root.append(section);
  const load = () => {
    section.innerHTML = "";
    section.append(el("h2", {}, "People from other organisations"));
    section.append(
      el(
        "p",
        { className: "caption" },
        "Users whose home is another organisation, given access to this one with their own role and teams here. " +
          "They switch to it from the Organisation dropdown. Revoking takes effect on their next request.",
      ),
    );
    getJson<MembershipList>(`${ADMIN}?include_revoked=true`)
      .then((doc) => {
        const live = doc.memberships.filter((m) => m.live);
        const past = doc.memberships.filter((m) => !m.live).slice(0, 20);
        if (live.length || past.length) section.append(membershipTable([...live, ...past], doc.roles, false, load));
        else section.append(el("p", { className: "caption" }, "Nobody yet."));
        section.append(el("h3", {}, "Give someone from another organisation access"));
        // org admins grant into their own organisation only (no picker); a
        // platform admin's picker defaults to the organisation in view
        section.append(grantForm(doc, {}, load));
      })
      .catch((e) => section.append(el("p", { className: "caption" }, `Couldn't load: ${errText(e)}`)));
  };
  load();
}
